import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EarlyPayChart } from '@/components/earlypay/EarlyPayChart';
import { EarlyPayTable } from '@/components/earlypay/EarlyPayTable';
import { UpcomingDiscountsSummary } from '@/components/earlypay/UpcomingDiscountsSummary';
import { Loader2, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { invoiceService, profileService, statusService } from '@/services';
import type { Status } from '@/services';

export default function EarlyPay() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  // Fetch user profile for company_id
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const result = await profileService.getCurrentProfile();
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  // Fetch invoice statuses
  const { data: statuses = [] } = useQuery({
    queryKey: ['statuses', 'invoice', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await statusService.listByEntityType('invoice', profile.company_id);
      if (result.error) throw result.error;
      return result.data || [];
    },
    enabled: !!profile?.company_id,
  });

  // Default to first status by seq_num once statuses load
  useEffect(() => {
    if (statuses.length > 0 && !selectedStatus) {
      setSelectedStatus(statuses[0].id);
    }
  }, [statuses, selectedStatus]);

  const { data: invoices, isLoading } = useQuery({
    queryKey: ['early-pay-invoices', profile?.company_id, selectedStatus],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await invoiceService.listForEarlyPay(profile.company_id, selectedStatus);
      if (result.error) throw result.error;
      return result.data || [];
    },
    enabled: !!profile?.company_id && !!selectedStatus,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const hasInvoices = invoices && invoices.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Early Pay Opportunities</h1>
        <p className="text-muted-foreground mt-1">
          Analyze discount opportunities across your invoices
        </p>
      </div>

      {/* Empty State */}
      {!hasInvoices && (
        <div className="flex flex-col items-center justify-center py-16 bg-card rounded-lg border border-border">
          <DollarSign className="w-12 h-12 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">
            No early pay opportunities found
          </h2>
          <p className="text-muted-foreground mb-4">
            Invoices with discount terms will appear here
          </p>
          <Button onClick={() => navigate('/tools/extract-invoice')}>
            Extract Invoice
          </Button>
        </div>
      )}

      {/* Dashboard Panels */}
      {hasInvoices && (
        <div className="space-y-6">
          {/* Chart + Summary Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Chart - 2/3 width */}
            <div className="lg:col-span-2">
              <EarlyPayChart invoices={invoices} />
            </div>

            {/* Summary - 1/3 width */}
            <div className="lg:col-span-1">
              <UpcomingDiscountsSummary invoices={invoices} />
            </div>
          </div>

          {/* Table Panel */}
          <EarlyPayTable
            invoices={invoices}
            statuses={statuses}
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
          />
        </div>
      )}
    </div>
  );
}
